import { useEffect, useState } from "react";
import { Radio, Calendar, Bell, BellOff, Clock, Users, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiErrorDetail } from "../lib/api";

function StatusBadge({ status }) {
  if (status === "live")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse">
        <Radio className="w-3 h-3" /> LIVE
      </span>
    );
  if (status === "ended")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-600 border border-slate-500/30">
        Beendet
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1D5BFF]/20 text-[#4DBFFF] border border-[#1D5BFF]/40">
      <Calendar className="w-3 h-3" /> Bevorstehend
    </span>
  );
}

function formatGermanDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function LiveCard({ s, onRemind, busy }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl overflow-hidden hover:border-[#1D5BFF]/50 transition-all" data-testid={`live-session-${s.id}`}>
      {s.image_url && (
        <div className="bg-[#F5F8FD] flex items-center justify-center" style={{ maxHeight: "320px" }}>
          <img src={fileUrl(s.image_url)} alt={s.title} className="w-full max-h-80 object-contain" />
        </div>
      )}
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h3 className="font-display text-lg sm:text-xl font-black text-slate-900 break-words flex-1 min-w-0">{s.title}</h3>
          <StatusBadge status={s.status} />
        </div>
        {s.host && (
          <div className="text-xs text-slate-600">
            mit <span className="text-[#4DBFFF] font-semibold">{s.host}</span>
          </div>
        )}
        {s.description && (
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{s.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#4DBFFF]" /> {formatGermanDate(s.starts_at)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {s.duration_minutes} Min
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> {s.reminders_count} erinnert
          </span>
        </div>
        {s.location && (
          <div className="text-xs text-slate-600">
            📍 <span className="text-slate-800">{s.location}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {s.link && s.status !== "ended" && (
            <a
              href={s.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`join-session-${s.id}`}
              className="bg-[#1D5BFF] hover:bg-[#1546C9] text-white font-semibold px-4 py-2 rounded-full text-sm flex items-center gap-2 shadow-[0_8px_24px_rgba(0,102,255,0.35)]"
            >
              {s.status === "live" ? <><Radio className="w-4 h-4 animate-pulse" /> Jetzt beitreten</> : <><ExternalLink className="w-4 h-4" /> Link öffnen</>}
            </a>
          )}
          {s.status !== "ended" && (
            <button
              onClick={() => onRemind(s)}
              disabled={busy}
              data-testid={`remind-session-${s.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition ${
                s.my_reminder
                  ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                  : "bg-slate-100/70 hover:bg-slate-100 border border-slate-200 text-slate-800"
              }`}
            >
              {s.my_reminder ? <><BellOff className="w-4 h-4" /> Erinnerung aktiv</> : <><Bell className="w-4 h-4" /> Erinnerung aktivieren</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Live() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/live-sessions");
      setSessions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const remind = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/live-sessions/${s.id}/remind`);
      setSessions((prev) => prev.map((x) => (x.id === s.id ? data : x)));
      toast.success(data.my_reminder ? "Erinnerung aktiviert 🔔" : "Erinnerung deaktiviert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    } finally {
      setBusy(null);
    }
  };

  const upcoming = sessions.filter((s) => s.status !== "ended");
  const ended = sessions.filter((s) => s.status === "ended");

  return (
    <div data-testid="live-page" className="space-y-5">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#4DBFFF] mb-1">Community</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter flex items-center gap-2">
          Live Talk & Q&A <Radio className="w-7 h-7 text-red-500" />
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-1">
          Triff das TSU-Team und andere Fahrer live – stelle Fragen, hol dir Tipps.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-10 text-center">
          <Radio className="w-12 h-12 text-[#1D5BFF] mx-auto mb-3" />
          <p className="text-slate-700 font-display text-lg">Aktuell keine Live-Session geplant</p>
          <p className="text-sm text-slate-500 mt-1">Sobald eine neue Session geplant wird, siehst du sie hier.</p>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {upcoming.map((s) => <LiveCard key={s.id} s={s} onRemind={remind} busy={busy === s.id} />)}
            </div>
          )}
          {ended.length > 0 && (
            <div className="pt-2">
              <h2 className="text-sm uppercase tracking-[0.2em] text-slate-600 mb-3">Vergangene Sessions</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {ended.map((s) => <LiveCard key={s.id} s={s} onRemind={remind} busy={busy === s.id} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
